import translator from '@Components/Utilities/Translator';
import TranslationText from '@Components/Utilities/TranslationText';
import { useState, useRef, useMemo } from 'react';
import { useRecoilValue } from 'recoil';
import { ThemeProvider } from '@emotion/react';
import { ThemedBox } from '@Components/MuiWrappers/ThemedBox';
import atoms from '@Components/Utilities/SandhillsAtoms';
import RequestAQuoteModal from '../Shared/Modals/RequestAQuoteModal';
import TextField from '@Components/Shared/Controls/TextField';
import { focusErroredField } from '@Components/Utilities/SandhillsUtility';
import LoadingRing from '@Components/MuiWrappers/LoadingRing';
import ValidationErrors from '@Components/Shared/ValidationErrors';
import { ValidationRules, ruleRunner, run } from '@Components/Utilities/Validation';
import Calendar from '@Components/MuiWrappers/Calendar';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';

const DetailRequestAQuoteBox = (props) => {
    const theme = useRecoilValue(atoms.muiThemes);
    const inputReferences = useRef({});
    const routes = useRecoilValue(atoms.routes);
    const [success, setSuccess] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [disableSubmit, setDisableSubmit] = useState(false);
    const [validationErrors, setValidationErrors] = useState({});
    const today = new Date();
    const [showRequestAQuoteModal, setShowRequestAQuoteModal] = useState(props.ShowRequestAQuoteModalOnLoad);
    const toggleRequestAQuoteModal = () => {
        setShowRequestAQuoteModal(showRequestAQuoteModal => !showRequestAQuoteModal);
    }

    const [formData, setFormData] = useState({
        FromDate: props.FromDate || new Date(),
        ToDate: props.ToDate || new Date(),
        Quantity: props.Quantity || '',
        Comments: props.Comments || ''
    });
    const submitForm = (e) => {
        e.preventDefault();
        let errors = run(formData, [
            ruleRunner('FromDate', translator.translate('sandhills.datefrom'), ValidationRules.required),
            ruleRunner('ToDate', translator.translate('sandhills.dateto'), ValidationRules.required),
            ruleRunner('Quantity', translator.translate('sandhills.quantity'), ValidationRules.required, ValidationRules.isWholeNumber, ValidationRules.mustBeGreaterThanEqualToValue(1, false)),
        ]);

        setValidationErrors(errors);
        focusErroredField(errors, inputReferences);
        setSubmitted(false);
        if (Object.keys(errors).length <= 0) {
            setSuccess(false);
            setDisableSubmit(true);
            onSubmit();
        }
    };
    const onSubmit = (token) => {
        setSubmitted(true);
        setDisableSubmit(false);
        setShowRequestAQuoteModal(true);
    };

    const onBeginDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, FromDate: date };
        });
    };
    const onEndDateChange = (date) => {
        setFormData(previousState => {
            return { ...previousState, ToDate: date };
        });
    };

    const getHeadingText = () => {
        return translator.translate('sandhills.requestaquote');
    }

    const onChange = (e) => {
        let name = e.target.name;
        let value = e.target.value;
        setFormData(previousState => {
            return { ...previousState, [name]: value };
        });
    };

    const sx = {
        '& .MuiOutlinedInput-root.Mui-focused': {
            '& > fieldset': {
                borderColor: 'black'
            }
        },
        backgroundColor: 'white',
        input: {
            fontSize: '14px',
            color: 'black'
        },
        button: {
            marginRight: '10px'
        },
        svg: {
            width: '15px',
            height: '15px'
        },
        width: '100%',
        height: '37.13px'
    };

    return (
        <ThemeProvider theme={(!theme.isHostedSite && theme.mainMode == "dark") ? theme.darkTheme : theme.lightTheme}>
            <ThemedBox>
                <div className='dealer-contact'>
                    <div className='request-a-quote-box-details'>
                        <div className='dealer-contact__header'>
                            <Typography variant='h2' className='dealer-contact__heading' dangerouslySetInnerHTML={{ __html: getHeadingText() }} sx={{ fontWeight: 'bold' }}></Typography>
                        </div>
                        <div className='request-quote-box__body'>
                            <div className='col col-6'>
                                <form id='detail-raq-form' className='form' onSubmit={submitForm}>
                                    <ValidationErrors errors={validationErrors} />
                                    <div className='form-row-one'>
                                        <div className='form-col'>
                                            <label htmlFor='FromDate' className='bold'><TranslationText tKey='Sandhills.DateFrom' /> <span className='required red' /></label>
                                            <div className='form-el'>
                                                <Calendar
                                                    id='FromDate'
                                                    name='FromDate'
                                                    value={formData.FromDate}
                                                    onChange={onBeginDateChange}
                                                    inputFormat='MM/dd/yyyy'
                                                    minDate={today}
                                                    InputAdornmentProps={{
                                                        sx: {
                                                            margin: '0px'
                                                        }
                                                    }}
                                                    sx={sx}
                                                />
                                            </div>
                                        </div>
                                        <div className='form-col'>
                                            <label className='bold' htmlFor='ToDate'><TranslationText tKey='Sandhills.DateTo' /> <span className='required red' /></label>
                                            <div className='form-el'>
                                                <Calendar
                                                    id='ToDate'
                                                    name='ToDate'
                                                    value={formData.ToDate}
                                                    onChange={onEndDateChange}
                                                    minDate={formData.FromDate}
                                                    inputFormat='MM/dd/yyyy'
                                                    InputAdornmentProps={{
                                                        sx: {
                                                            margin: '0px'
                                                        }
                                                    }}
                                                    sx={sx}
                                                />
                                            </div>
                                        </div>
                                        <div className='form-col'>
                                            <label htmlFor='Quantity' className='bold'><TranslationText tKey='sandhills.quantity' /> <span className='required red' /></label>
                                            <TextField
                                                id='Quantity'
                                                name='Quantity'
                                                value={formData.Quantity}
                                                type='text'
                                                class='form-el'
                                                required={true}
                                                validationErrors={validationErrors.Quantity}
                                                ref={(element) => inputReferences.current[`Quantity`] = element}
                                                onFieldChanged={onChange}
                                            />
                                        </div>
                                    </div>
                                    <div className='form-row-two'>
                                        <div className='form-col comments'>
                                            <label htmlFor='Comments' className='bold'><TranslationText tKey='sandhills.tradepubwebsite.mvc.comments' /></label>
                                            <TextField
                                                id='Comments'
                                                name='Comments'
                                                value={formData.Comments}
                                                type='textarea'
                                                class='text-area-container'
                                                inputClass='text-area'
                                                required={true}
                                                validationErrors={validationErrors.Comments}
                                                ref={(element) => inputReferences.current[`Comments`] = element}
                                                onFieldChanged={onChange}
                                            />
                                        </div>
                                        <div className='form-col submit'>
                                            <Button variant="contained" className='button ts-button' type='submit' id='request-a-quote-submit-button-detail' name='request-a-quote-submit-button-detail' disabled={disableSubmit}>{translator.translate('sandhills.tradepubwebsite.mvc.submit')}</Button>
                                            {disableSubmit && <LoadingRing backdrop />}
                                        </div>
                                    </div>
                                </form>
                                {showRequestAQuoteModal &&
                                    <RequestAQuoteModal
                                    {...props.RequestAQuote}
                                    toggleModal={toggleRequestAQuoteModal}
                                    showRequestAQuoteModal={showRequestAQuoteModal}
                                    ListingTitle={props.ListingTitle}
                                    ListingID={props.ListingID}
                                    Manufacturer={props.Manufacturer}
                                    Category={props.Category}
                                    Industry={props.Industry}
                                    Comments={formData.Comments}
                                    Model={props.Model}
                                    FromDate={formData.FromDate}
                                    ToDate={formData.ToDate}
                                    Quantity={formData.Quantity}
                                    ShouldPopulateSelectListOnLoad={false}
                                    DirectMessageToCRMID={props.DirectMessageToCRMID}
                                    DisableIndustries={true}
                                    DisableCategories={true}

                                    />
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </ThemedBox>
        </ThemeProvider>
    );
};

export default DetailRequestAQuoteBox;